import { useCallback } from "react";
import { useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import {
  uploadDocuments as uploadDocumentsApi,
  processDocument,
  listProposalDocuments,
  downloadRfpFile,
} from "../api/client";

import type {
  ListProposalDocumentsResponse,
  ProcessDocumentResponse,
  ProposalDocumentResponse,
  UploadDocumentsResponse,
} from "../api/types";
import type { Carrier, CarrierProposal } from "../types";

const PROPOSAL_DOCUMENTS_QUERY_KEY = "proposalDocuments";

export const getProposalDocumentsCacheKey = (rfpId: string): string[] => [
  PROPOSAL_DOCUMENTS_QUERY_KEY,
  rfpId,
];

interface UseUploadDocsOptions {
  ecoRfpId: string;
  brokerId: number | undefined;
  brokerPortalRfpId: number | undefined;
  onSuccess?: () => void;
}

interface UploadDocsMutationVariables {
  carrier: string;
  files: File[];
}

interface UseUploadDocumentsResult {
  uploadDocuments: (carrier: string, files: File[]) => void;
  loading: boolean;
  error: string | null;
  clearError: VoidFunction;
}

export function useUploadDocuments(
  options: UseUploadDocsOptions,
): UseUploadDocumentsResult {
  const mutation = useUploadDocsMutation(options);

  const uploadDocuments = useCallback(
    (carrier: string, files: File[]) => {
      mutation.mutate({ carrier, files });
    },
    [mutation],
  );

  return {
    uploadDocuments,
    loading: mutation.isPending,
    error: mutation.error?.message ?? null,
    clearError: mutation.reset,
  };
}

function useUploadDocsMutation({
  ecoRfpId,
  brokerId,
  brokerPortalRfpId,
  onSuccess,
}: UseUploadDocsOptions): UseMutationResult<
  UploadDocumentsResponse,
  Error,
  UploadDocsMutationVariables
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    UploadDocumentsResponse,
    Error,
    UploadDocsMutationVariables
  >({
    mutationFn: async ({ carrier, files }) => {
      if (
        !ecoRfpId ||
        !Number.isFinite(brokerId) ||
        !Number.isFinite(brokerPortalRfpId) ||
        brokerId === undefined ||
        brokerPortalRfpId === undefined
      ) {
        throw new Error("Cannot upload: missing RFP or broker identifiers");
      }

      return uploadDocumentsApi(
        ecoRfpId,
        brokerId,
        brokerPortalRfpId,
        files,
        axiosInstance,
        carrier,
      );
    },
    onSuccess: async (response, variables) => {
      await Promise.allSettled(
        response.proposals.map((doc) =>
          processDocument(ecoRfpId, doc.id, axiosInstance).catch((err) => {
            logger.error(
              `[PropelX Documents] Failed to dispatch process for documentId=${doc.id}`,
              err,
            );
          }),
        ),
      );

      await queryClient.invalidateQueries({
        queryKey: getProposalDocumentsCacheKey(ecoRfpId),
      });

      onSuccess?.();
      logger.info(
        "[PropelX Documents] Documents uploaded and processing dispatched",
        {
          ecoRfpId,
          carrier: variables.carrier,
          count: variables.files.length,
        },
      );
    },
    onError: (error) => {
      logger.error(
        `[PropelX Documents] Upload failed for rfpId=${ecoRfpId}`,
        error,
      );
    },
  });
}

interface UseProposalDocumentsResult {
  proposals: CarrierProposal[];
  loading: boolean;
  error: string | null;
  timeoutFlag: boolean;
  refetch: VoidFunction;
  clearTimeoutFlag: VoidFunction;
}

const REFETCH_INTERVAL_IN_MILLISECONDS = 10000;

const mapProposalStatus = (
  status: string,
): "Error" | "Extracted" | "Processing" => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === "extracted") {
    return "Extracted";
  }

  if (normalizedStatus === "error" || normalizedStatus === "excluded") {
    return "Error";
  }

  return "Processing";
};

const mapProposalDocument = (
  document: ProposalDocumentResponse,
): CarrierProposal["documents"][number] => ({
  id: document.id,
  name: document.name,
  createdAt: document.file?.uploadedAt ?? "-",
  downloadUrl: document.file?.url ?? null,
  status: mapProposalStatus(document.status),
  errorCode: document.errorCode ?? document.excludedCode,
});

const mapProposalDocumentsResponse = (
  response: ListProposalDocumentsResponse,
  carriers: Carrier[] = [],
): CarrierProposal[] =>
  response.carriers.map((carrierGroup) => ({
    carrier:
      carriers.find((c) => c.id === carrierGroup.carrierIdentifier)?.name ??
      `unknown carrierIdentifier: ${carrierGroup.carrierIdentifier}`,
    documents: carrierGroup.documents.map(mapProposalDocument),
  }));

export function useProposalDocumentsQuery(
  rfpId: string,
  carriers: Carrier[] = [],
): UseProposalDocumentsResult {
  const axiosInstance = useApi();

  const clearTimeoutFlag = useCallback(() => {
    // Timeout simulation was removed with mock data.
  }, []);

  const query = useQuery<
    ListProposalDocumentsResponse,
    Error,
    CarrierProposal[]
  >({
    enabled: !!rfpId,
    queryKey: getProposalDocumentsCacheKey(rfpId),
    queryFn: () => listProposalDocuments(rfpId, axiosInstance),
    select: (response) => mapProposalDocumentsResponse(response, carriers),
    refetchInterval: (q): number | false => {
      const hasProcessing = q.state.data?.carriers.some((carrierGroup) =>
        carrierGroup.documents.some(
          (doc) => mapProposalStatus(doc.status) === "Processing",
        ),
      );

      return hasProcessing ? REFETCH_INTERVAL_IN_MILLISECONDS : false;
    },
  });

  return {
    proposals: query.data ?? [],
    loading: query.isFetching,
    error: query.error?.message ?? null,
    timeoutFlag: false,
    refetch: query.refetch,
    clearTimeoutFlag,
  };
}

interface UseReprocessDocumentResult {
  reprocess: (documentId: string) => void;
  reprocessingId: string | null;
  isReprocessing: boolean;
}

/**
 * Re-dispatches AI processing for a document that failed with a retriable code.
 * On success the proposals query is invalidated so the document flips back to
 * Processing; `reprocessingId` exposes the document currently being re-run so a
 * single row can show its own pending state, and `isReprocessing` reports
 * whether any re-run is in flight.
 */
export function useReprocessDocument(
  ecoRfpId: string,
): UseReprocessDocumentResult {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  const mutation = useMutation<ProcessDocumentResponse, Error, string>({
    mutationFn: (documentId) =>
      processDocument(ecoRfpId, documentId, axiosInstance),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getProposalDocumentsCacheKey(ecoRfpId),
      });
    },
    onError: (error, documentId) => {
      logger.error(
        `[PropelX Documents] Re-run failed for documentId=${documentId}`,
        error,
      );
    },
  });

  const reprocess = useCallback(
    (documentId: string) => {
      if (mutation.isPending) return;
      mutation.mutate(documentId);
    },
    [mutation],
  );

  return {
    reprocess,
    reprocessingId: mutation.isPending ? mutation.variables : null,
    isReprocessing: mutation.isPending,
  };
}

export function useDownloadDocumentMutation(): UseMutationResult<
  Blob,
  Error,
  { downloadUrl: string }
> {
  const axiosInstance = useApi();

  return useMutation<Blob, Error, { downloadUrl: string }>({
    mutationFn: async ({ downloadUrl }) =>
      downloadRfpFile(downloadUrl, axiosInstance),
    onError: (error) => {
      logger.error("[PropelX Documents] Download failed", error);
    },
  });
}
